import { ENDPOINT_NAMES, endpoints } from '../../config/endpoints';
import { addDays, endOfDay, startOfToday } from 'date-fns';
import DEFAULT_REFETCH_INTERVAL from '../../constants/default-refetch-interval';
import React from 'react';
import SORT_ORDER from '../../constants/sort-order';
import ScheduledEvent from '../../models/day-orchestrator/scheduled-event';
import USER_TYPE from '../../constants/user-type';
import { useFetch } from '@llt-frontend/react-fetch';
import useUserContext from '../context/use-user-context';

function _getEndTimeParam() {
  const start = startOfToday();
  return endOfDay(addDays(start, 90)).toISOString();
}

function _getStartTimeParam() {
  const start = startOfToday();
  return start.toISOString();
}

function _serializeEventData(data, activeStudent) {
  if (!data) {
    return [];
  }

  if (data.pages) {
    // Handle paged data
    data = data.pages.flat();
  }

  return data
    .map((event) => ScheduledEvent(event, activeStudent.uuid))
    .filter(Boolean)
    .filter(
      // We get all events for the logged in client, so we need to filter out any events that
      // aren't applicable to the active student.
      ({ online, student: { firstName, lastName, uuid } = {} }) =>
        online &&
        (uuid === activeStudent.uuid ||
          // Student UUIDs aren't always available in tutoring session event data, so providing
          // a name check as a fallback.
          (firstName === activeStudent.firstName && lastName === activeStudent.lastName))
    );
}

//

function useStudentSchedule({
  endTime,
  infinite = false,
  limit = 10,
  refetchInterval = DEFAULT_REFETCH_INTERVAL,
  timeOrder = SORT_ORDER.ascending,
  startTime,
} = {}) {
  const {
    activeStudent,
    activeStudentId,
    activeUser: { type: userType, uuid: clientUuid } = {},
  } = useUserContext();

  const endpoint = endpoints.get(ENDPOINT_NAMES.studentSchedule, {
    studentId: activeStudentId,
    userType,
  });
  const startTimeParam = React.useMemo(() => startTime || _getStartTimeParam(), [startTime]);
  const endTimeParam = React.useMemo(() => endTime || _getEndTimeParam(), [endTime]);

  const commonParams = {
    starttime: startTimeParam,
  };

  if (userType !== USER_TYPE.student) {
    commonParams['client_id'] = clientUuid;
  }

  const schedule = useFetch(endpoint.path, {
    infinite,
    params: {
      ...commonParams,
      endtime: endTimeParam,
      limit,
      sort: `${timeOrder === SORT_ORDER.descending ? '-' : ''}time`,
    },
    queryKey: [...endpoint.key, startTimeParam, endTimeParam, limit, timeOrder],
    queryOptions: {
      getNextPageParam(lastPage, allPages) {
        return !lastPage.length || lastPage.length < limit ? undefined : allPages.length + 1;
      },
      refetchInterval,
    },
    requestOptions: {
      pageParamName: 'page',
    },
  });

  const serializedEventData = React.useMemo(
    () => _serializeEventData(schedule.data, activeStudent),
    [schedule.data, activeStudent]
  );

  //
  // Result

  return {
    fetchNextPage: schedule.fetchNextPage,
    fetchingNextPage: schedule.isFetchingNextPage,
    hasNextPage: schedule.hasNextPage,
    loading: schedule.isLoading,
    schedule: serializedEventData,
  };
}

// Exports
// -------

export default useStudentSchedule;
